import { X } from "lucide-react"
import * as React from "react"
import { cn } from "@web/lib/utils"

const ToastContext = React.createContext<{
	toasts: Array<{
		id: string
		message: string
		type: "success" | "error" | "info"
	}>
	addToast: (message: string, type?: "success" | "error" | "info") => void
	removeToast: (id: string) => void
}>({
	toasts: [],
	addToast: () => {},
	removeToast: () => {},
})

export function ToastProvider({ children }: { children: React.ReactNode }) {
	const [toasts, setToasts] = React.useState<
		Array<{ id: string; message: string; type: "success" | "error" | "info" }>
	>([])

	const addToast = React.useCallback(
		(message: string, type: "success" | "error" | "info" = "info") => {
			const id = Math.random().toString(36).substr(2, 9)
			setToasts((prev) => [...prev, { id, message, type }])
			setTimeout(() => removeToast(id), 5000)
		},
		[removeToast],
	)

	const removeToast = React.useCallback((id: string) => {
		setToasts((prev) => prev.filter((toast) => toast.id !== id))
	}, [])

	return (
		<ToastContext.Provider value={{ toasts, addToast, removeToast }}>
			{children}
			<div className="fixed top-4 right-4 z-50 space-y-2">
				{toasts.map((toast) => (
					<div
						key={toast.id}
						className={cn(
							"flex items-center justify-between p-4 rounded-lg shadow-lg min-w-[300px] animate-in slide-in-from-right",
							{
								"bg-green-500 text-white": toast.type === "success",
								"bg-red-500 text-white": toast.type === "error",
								"bg-blue-500 text-white": toast.type === "info",
							},
						)}>
						<span>{toast.message}</span>
						<button onClick={() => removeToast(toast.id)} className="ml-2 hover:opacity-70">
							<X className="h-4 w-4" />
						</button>
					</div>
				))}
			</div>
		</ToastContext.Provider>
	)
}

export const useToast = () => React.useContext(ToastContext)
